import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Camera, Save, MapPin, Briefcase, Phone, Mail, Globe, Plus, X,
  CheckCircle, AlertCircle, Facebook, Instagram, Linkedin, Youtube,
  ChevronDown, Building, Clock, MessageSquare, Upload, Image
} from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import GradientButton from '@/components/ui/GradientButton'
import Input from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import { agentService } from '@/services/agentService'
import { toast } from 'react-toastify'
import { COUNTRY_CODES, getCountryByCode, DEFAULT_COUNTRY_CODE } from '@/constants/countryCodes'

// ============================================================
// CHECKBOX DE VISIBILIDAD
// disabled: campo vacío — no se puede mostrar algo que no existe
// ============================================================
interface VisibilityCheckboxProps {
  id: string
  value: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  label: string
  hint?: string
  disabledTitle?: string
}
const VisibilityCheckbox: React.FC<VisibilityCheckboxProps> = ({
  id, value, onChange, disabled = false, label, hint, disabledTitle
}) => (
  <label
    htmlFor={id}
    className={`flex items-center gap-2 cursor-pointer select-none
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90'}`}
    title={disabled ? disabledTitle : undefined}
  >
    <input
      id={id}
      type="checkbox"
      checked={value}
      disabled={disabled}
      onChange={(e) => !disabled && onChange(e.target.checked)}
      className="w-4 h-4 rounded accent-blue-500 cursor-pointer disabled:cursor-not-allowed"
    />
    <span className={`text-xs ${value ? 'text-green-400' : 'text-white/50'}`}>
      {label}
    </span>
    {hint && <span className="text-white/30 text-xs">· {hint}</span>}
  </label>
)

// ============================================================
// SECCIÓN COLAPSABLE
// ============================================================
interface SectionProps {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
}
const Section: React.FC<SectionProps> = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="text-white font-semibold text-sm">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 py-5 space-y-5">{children}</div>}
    </div>
  )
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const ProfilePage: React.FC = () => {
  const { t } = useTranslation('profile')
  const { agent, updateAgent } = useAuth()
  const [loading, setLoading]               = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [logoUploading, setLogoUploading]   = useState(false)
  const [logoUrl, setLogoUrl]               = useState<string | null>(null)
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE)
  const [phoneNumber, setPhoneNumber]       = useState('')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [phoneError, setPhoneError]         = useState('')

  const [formData, setFormData] = useState({
    name:               '',
    bio:                '',
    specialties:        [] as string[],
    zones:              [] as string[],
    social_media: { facebook: '', instagram: '', linkedin: '', tiktok: '', youtube: '' },
    // Profesional
    business_name:      '',
    physical_address:   '',
    attention_hours:    '',
    complaints_contact: '',
    tax_id:             '',
    // Visibilidad — fuente de verdad para templates y NOM
    show_phone:         false,
    show_email:         false,
    show_address:       false,
    show_attention_hours:    false,
    show_complaints_contact: false,
    show_avatar:        true,
    show_logo:          false,
    // Nombre visible — al menos uno siempre true
    show_name:          true,
    show_business_name: false,
    // Documentos legales en footer (show_* = fuente de verdad, is_published en agent_legal_docs es redundante/auditoría)
    show_aviso_privacidad:    false,
    show_terminos:            false,
    show_derechos_consumidor: false,
    show_info_nom247:         false,  // Solo visible en UI si nom_candidate = true
  })

  const [newSpecialty, setNewSpecialty] = useState('')
  const [newZone, setNewZone]           = useState('')

  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]

  // ── Parsear teléfono ──
  const parsePhoneNumber = (fullPhone: string) => {
    if (!fullPhone) return { code: '+52', number: '' }
    for (const country of COUNTRY_CODES) {
      if (fullPhone.startsWith(country.code))
        return { code: country.code, number: fullPhone.slice(country.code.length) }
      const noPlus = country.code.replace('+', '')
      if (fullPhone.startsWith(noPlus))
        return { code: country.code, number: fullPhone.slice(noPlus.length) }
    }
    const match = fullPhone.match(/^(\+\d{1,3})(.*)$/)
    if (match) return { code: match[1], number: match[2] }
    return { code: '+52', number: fullPhone.replace(/\D/g, '') }
  }

  // ── Cargar perfil ──
  useEffect(() => {
    const load = async () => {
      try {
        const p = await agentService.getProfile()
        const { code, number } = parsePhoneNumber(p.phone || '')
        setCountryCode(code)
        setPhoneNumber(number)
        setLogoUrl(p.logo_url || null)
        setFormData({
          name:               p.name               || '',
          bio:                p.bio                || '',
          specialties:        p.specialties        || [],
          zones:              p.zones              || [],
          social_media:       p.social_media       || { facebook: '', instagram: '', linkedin: '', tiktok: '', youtube: '' },
          business_name:      p.business_name      || '',
          physical_address:   p.physical_address   || '',
          attention_hours:    p.attention_hours    || '',
          complaints_contact: p.complaints_contact || '',
          tax_id:             p.tax_id             || '',
          show_phone:         p.show_phone         ?? false,
          show_email:         p.show_email         ?? false,
          show_address:       p.show_address       ?? false,
          show_attention_hours:    p.show_attention_hours    ?? false,
          show_complaints_contact: p.show_complaints_contact ?? false,
          show_avatar:        p.show_avatar        ?? true,
          show_logo:          p.show_logo          ?? false,
          show_name:          p.show_name          ?? true,
          show_business_name: p.show_business_name ?? false,
          show_aviso_privacidad:    p.show_aviso_privacidad    ?? false,
          show_terminos:            p.show_terminos            ?? false,
          show_derechos_consumidor: p.show_derechos_consumidor ?? false,
          show_info_nom247:         p.show_info_nom247         ?? false,
        })
      } catch {
        toast.error(t('errorLoading'))
      } finally {
        setInitialLoading(false)
      }
    }
    load()
  }, [])

  // ── Validar teléfono ──
  const validatePhone = (): boolean => {
    const digits = phoneNumber.replace(/\D/g, '')
    if (!digits) { setPhoneError(t('phoneRequired')); return false }
    if (digits.length !== selectedCountry.digits) {
      setPhoneError(t('phoneDigits', { digits: selectedCountry.digits, country: selectedCountry.country }))
      return false
    }
    setPhoneError('')
    return true
  }

  // ── Regla: al menos show_name o show_business_name activo ──
  const setShowName = (field: 'show_name' | 'show_business_name', value: boolean) => {
    if (!value && field === 'show_name'          && !formData.show_business_name) return
    if (!value && field === 'show_business_name' && !formData.show_name)          return
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // ── Sincronizar is_published en agent_legal_docs ──
  // show_* es la fuente de verdad; is_published es campo redundante de auditoría
  const syncLegalDocs = async (data: typeof formData) => {
    const docMap: Record<string, boolean> = {
      aviso_privacidad:    data.show_aviso_privacidad,
      terminos_condiciones: data.show_terminos,
      derechos_consumidor: data.show_derechos_consumidor,
      info_nom247:         data.show_info_nom247,
    }
    await Promise.allSettled(
      Object.entries(docMap).map(([docType, is_published]) =>
        fetch(`/api/agents/nom/legal-docs/${docType}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token') || ''}`
          },
          body: JSON.stringify({ is_published })
        })
      )
    )
  }

  // ── Guardar perfil ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validatePhone()) return
    setLoading(true)
    try {
      const dataToSend = {
        ...formData,
        phone: countryCode + phoneNumber,
      }
      const response = await agentService.updateProfile(dataToSend)
      if (agent) updateAgent({ ...agent, ...response })
      // Sincronizar is_published en agent_legal_docs (campo redundante de auditoría)
      await syncLegalDocs(formData)
      toast.success(t('profileUpdated'))
    } catch {
      toast.error(t('errorUpdating'))
    } finally {
      setLoading(false)
    }
  }

  // ── Dropzone avatar ──
  const onDropAvatar = async (files: File[]) => {
    if (!files[0]) return
    setAvatarUploading(true)
    try {
      const res = await agentService.uploadAvatar(files[0])
      if (agent) updateAgent({ ...agent, avatar_url: res.avatarUrl })
      toast.success(t('photoUpdated'))
    } catch { toast.error(t('errorPhoto')) }
    finally { setAvatarUploading(false) }
  }
  const { getRootProps: avatarRoot, getInputProps: avatarInput, isDragActive: avatarDrag } = useDropzone({
    onDrop: onDropAvatar, accept: { 'image/*': ['.jpg','.jpeg','.png','.gif'] }, maxSize: 10485760, multiple: false
  })

  // ── Dropzone logo ──
  const onDropLogo = async (files: File[]) => {
    if (!files[0]) return
    setLogoUploading(true)
    try {
      const res = await agentService.uploadLogo(files[0])
      setLogoUrl(res.logoUrl)
      toast.success(t('logoUpdated'))
    } catch { toast.error(t('errorLogo')) }
    finally { setLogoUploading(false) }
  }
  const { getRootProps: logoRoot, getInputProps: logoInput, isDragActive: logoDrag } = useDropzone({
    onDrop: onDropLogo, accept: { 'image/*': ['.jpg','.jpeg','.png','.svg','.webp'] }, maxSize: 5242880, multiple: false
  })

  const addItem = (field: 'specialties' | 'zones', value: string, setValue: (v: string) => void) => {
    const trimmed = value.trim()
    if (trimmed && !(formData[field] as string[]).includes(trimmed))
      setFormData(prev => ({ ...prev, [field]: [...(prev[field] as string[]), trimmed] }))
    setValue('')
  }
  const removeItem = (field: 'specialties' | 'zones', index: number) =>
    setFormData(prev => ({ ...prev, [field]: (prev[field] as string[]).filter((_, i) => i !== index) }))

  if (initialLoading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    </DashboardLayout>
  )

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 pb-10">

        {/* ── Título ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <h1 className="text-3xl font-bold text-white mb-1">{t('title')}</h1>
          <p className="text-white/50 text-sm">{t('subtitle')}</p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ══════════════════════════════════════════════
              SECCIÓN: Imágenes
          ══════════════════════════════════════════════ */}
          <Section title={t('sectionImages')} icon={<Camera className="w-4 h-4 text-cyan-400" />}>
            <div className="grid grid-cols-2 gap-6">

              {/* Foto de perfil */}
              <div className="flex flex-col items-center gap-3">
                <p className="text-xs font-semibold text-white/60 uppercase tracking-wider self-start">{t('profilePhoto')}</p>
                <div
                  {...avatarRoot()}
                  className={`relative w-28 h-28 rounded-full overflow-hidden cursor-pointer
                    border-4 border-dashed transition-all duration-300 group
                    ${avatarDrag ? 'border-blue-500 bg-blue-500/20' : 'border-white/20 hover:border-blue-500/50'}`}
                >
                  <input {...avatarInput()} />
                  {agent?.avatar_url
                    ? <img src={agent.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <span className="text-3xl font-bold text-white">{formData.name?.charAt(0) || '?'}</span>
                      </div>
                  }
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {avatarUploading
                      ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                      : <Camera className="h-6 w-6 text-white" />
                    }
                  </div>
                </div>
                <VisibilityCheckbox
                  id="show_avatar"
                  value={formData.show_avatar}
                  onChange={(v) => setFormData(prev => ({ ...prev, show_avatar: v }))}
                  disabled={!agent?.avatar_url}
                  label={t('visibleOnSite')}
                  disabledTitle={t('captureFieldFirst')}
                />
                <p className="text-white/30 text-xs">{t('avatarHint')}</p>
              </div>

              {/* Logo */}
              <div className="flex flex-col items-center gap-3">
                <p className="text-xs font-semibold text-white/60 uppercase tracking-wider self-start">{t('companyLogo')}</p>
                <div
                  {...logoRoot()}
                  className={`relative w-full h-28 rounded-xl overflow-hidden cursor-pointer
                    border-2 border-dashed transition-all duration-300 group flex items-center justify-center
                    ${logoDrag ? 'border-purple-500 bg-purple-500/20' : 'border-white/20 hover:border-purple-500/50 bg-white/5'}`}
                >
                  <input {...logoInput()} />
                  {logoUrl
                    ? <>
                        <img src={logoUrl} alt="Logo" className="max-h-24 max-w-full object-contain p-2" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          {logoUploading
                            ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                            : <Upload className="h-6 w-6 text-white" />
                          }
                        </div>
                      </>
                    : <div className="text-center">
                        {logoUploading
                          ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto" />
                          : <><Image className="h-8 w-8 text-white/20 mx-auto mb-1" /><p className="text-white/30 text-xs">{t('dragOrClick')}</p></>
                        }
                      </div>
                  }
                </div>
                <VisibilityCheckbox
                  id="show_logo"
                  value={formData.show_logo}
                  onChange={(v) => setFormData(prev => ({ ...prev, show_logo: v }))}
                  disabled={!logoUrl}
                  label={t('visibleOnSite')}
                  disabledTitle={t('captureFieldFirst')}
                />
                <p className="text-white/30 text-xs">{t('logoHint')}</p>
              </div>

            </div>
          </Section>

          {/* ══════════════════════════════════════════════
              SECCIÓN: Información General
          ══════════════════════════════════════════════ */}
          <Section title={t('sectionGeneral')} icon={<Mail className="w-4 h-4 text-blue-400" />}>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email — no editable */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5">
                  <Mail className="inline w-3.5 h-3.5 mr-1" />{t('email')}
                </label>
                <Input value={agent?.email || ''} disabled className="bg-white/5 opacity-60" />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-white/30 text-xs">{t('emailCantChange')}</p>
                  <VisibilityCheckbox
                    id="show_email"
                    value={formData.show_email}
                    onChange={(v) => setFormData(prev => ({ ...prev, show_email: v }))}
                    disabled={!agent?.email}
                    label={t('visibleOnSite')}
                    disabledTitle={t('captureFieldFirst')}
                  />
                </div>
              </div>

              {/* Nombre */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5">
                  <Briefcase className="inline w-3.5 h-3.5 mr-1" />{t('fullName')}
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('fullNamePlaceholder')}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-white/30 text-xs">{t('personalName')}</p>
                  <VisibilityCheckbox
                    id="show_name"
                    value={formData.show_name}
                    onChange={(v) => setShowName('show_name', v)}
                    disabled={false}
                    label={t('visibleOnSite')}
                    hint={!formData.show_business_name ? t('required') : undefined}
                  />
                </div>
              </div>
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-1.5">
                <Phone className="inline w-3.5 h-3.5 mr-1" />{t('phone')}
              </label>
              <div className="flex gap-2">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    className="flex items-center gap-2 px-3 py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/20 transition-colors min-w-[110px]"
                  >
                    <span>{selectedCountry.flag}</span>
                    <span className="font-medium text-sm">{selectedCountry.code}</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showCountryDropdown && (
                    <div className="absolute z-50 mt-2 w-64 bg-slate-800 border border-white/20 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                      {COUNTRY_CODES.map((c) => (
                        <button key={c.code} type="button"
                          onClick={() => { setCountryCode(c.code); setShowCountryDropdown(false); setPhoneNumber(''); setPhoneError('') }}
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors text-left
                            ${countryCode === c.code ? 'bg-blue-500/20 border-l-4 border-blue-500' : ''}`}
                        >
                          <span>{c.flag}</span>
                          <span className="text-white text-sm">{c.country}</span>
                          <span className="text-white/40 text-xs ml-auto">{c.code}</span>
                          {countryCode === c.code && <CheckCircle className="h-3.5 w-3.5 text-blue-400" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '')
                    if (v.length <= selectedCountry.digits) { setPhoneNumber(v); setPhoneError('') }
                  }}
                  placeholder={selectedCountry.format}
                  className={phoneError ? 'border-red-500' : ''}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                {phoneError
                  ? <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="h-3 w-3" />{phoneError}</p>
                  : <p className="text-white/30 text-xs">{t('phoneHint', { digits: selectedCountry.digits, format: selectedCountry.format })}</p>
                }
                <VisibilityCheckbox
                  id="show_phone"
                  value={formData.show_phone}
                  onChange={(v) => setFormData(prev => ({ ...prev, show_phone: v }))}
                  disabled={!phoneNumber.trim()}
                  label={t('visibleOnSite')}
                  disabledTitle={t('captureFieldFirst')}
                />
              </div>
            </div>

            {/* Biografía */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-1.5">
                <Globe className="inline w-3.5 h-3.5 mr-1" />{t('bio')}
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder={t('bioPlaceholder')}
                rows={3}
                className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white text-sm placeholder-white/30 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
              />
            </div>

          </Section>

          {/* ══════════════════════════════════════════════
              SECCIÓN: Datos Profesionales y Empresa
          ══════════════════════════════════════════════ */}
          <Section title={t('sectionProfessional')} icon={<Building className="w-4 h-4 text-purple-400" />}>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Nombre Comercial */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5">
                  <Building className="inline w-3.5 h-3.5 mr-1" />{t('businessName')}
                </label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder={t('businessNamePlaceholder')}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-white/30 text-xs">{t('businessNameHint')}</p>
                  <VisibilityCheckbox
                    id="show_business_name"
                    value={formData.show_business_name}
                    onChange={(v) => setShowName('show_business_name', v)}
                    disabled={!formData.business_name.trim()}
                    label={t('visibleOnSite')}
                    hint={!formData.show_name ? t('required') : undefined}
                  />
                </div>
              </div>

              {/* RFC */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5">{t('taxId')}</label>
                <Input
                  value={formData.tax_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, tax_id: e.target.value }))}
                  placeholder="XAXX010101000"
                />
                <p className="text-white/30 text-xs mt-1">{t('taxIdHint')}</p>
              </div>

              {/* Domicilio */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5">
                  <MapPin className="inline w-3.5 h-3.5 mr-1" />{t('address')}
                </label>
                <Input
                  value={formData.physical_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, physical_address: e.target.value }))}
                  placeholder={t('addressPlaceholder')}
                />
                <div className="flex items-center justify-end mt-1">
                  <VisibilityCheckbox
                    id="show_address"
                    value={formData.show_address}
                    onChange={(v) => setFormData(prev => ({ ...prev, show_address: v }))}
                    disabled={!formData.physical_address.trim()}
                    label={t('visibleOnSite')}
                    disabledTitle={t('captureFieldFirst')}
                  />
                </div>
              </div>

              {/* Horario */}
              <div>
                <label className="block text-white/60 text-xs font-medium mb-1.5">
                  <Clock className="inline w-3.5 h-3.5 mr-1" />{t('attentionHours')}
                </label>
                <Input
                  value={formData.attention_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, attention_hours: e.target.value }))}
                  placeholder={t('attentionHoursPlaceholder')}
                />
                <div className="flex items-center justify-end mt-1">
                  <VisibilityCheckbox
                    id="show_attention_hours"
                    value={formData.show_attention_hours}
                    onChange={(v) => setFormData(prev => ({ ...prev, show_attention_hours: v }))}
                    disabled={!formData.attention_hours.trim()}
                    label={t('visibleOnSite')}
                    disabledTitle={t('captureFieldFirst')}
                  />
                </div>
              </div>

            </div>

            {/* Canal de atención */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-1.5">
                <MessageSquare className="inline w-3.5 h-3.5 mr-1" />{t('complaintsContact')}
              </label>
              <Input
                value={formData.complaints_contact}
                onChange={(e) => setFormData(prev => ({ ...prev, complaints_contact: e.target.value }))}
                placeholder={t('complaintsContactPlaceholder')}
              />
              <div className="flex items-center justify-end mt-1">
                <VisibilityCheckbox
                  id="show_complaints_contact"
                  value={formData.show_complaints_contact}
                  onChange={(v) => setFormData(prev => ({ ...prev, show_complaints_contact: v }))}
                  disabled={!formData.complaints_contact.trim()}
                  label={t('visibleOnSite')}
                  disabledTitle={t('captureFieldFirst')}
                />
              </div>
            </div>

          </Section>

          {/* ══════════════════════════════════════════════
              SECCIÓN: Documentos Legales en Footer
              Disponible para todos los agentes
              show_info_nom247 solo si nom_candidate = true
          ══════════════════════════════════════════════ */}
          <Section title={t('sectionLegal')} icon={<Briefcase className="w-4 h-4 text-amber-400" />} defaultOpen={false}>
            <p className="text-white/40 text-xs -mt-2">
              {t('legalIntro')}
            </p>
            <div className="space-y-4">

              {/* Aviso de Privacidad */}
              <div className="flex items-start justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div>
                  <p className="text-white/80 text-sm font-medium">{t('avisoPrivacidad')}</p>
                  <p className="text-white/40 text-xs mt-0.5">{t('avisoPrivacidadDesc')}</p>
                  {!(formData.physical_address && agent?.phone && agent?.email) && (
                    <p className="text-amber-400/70 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />{t('avisoPrivacidadReq')}
                    </p>
                  )}
                </div>
                <VisibilityCheckbox
                  id="show_aviso_privacidad"
                  value={formData.show_aviso_privacidad}
                  onChange={(v) => setFormData(prev => ({ ...prev, show_aviso_privacidad: v }))}
                  disabled={!(formData.physical_address && agent?.phone && agent?.email)}
                  label={t('show')}
                />
              </div>

              {/* Términos y Condiciones */}
              <div className="flex items-start justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div>
                  <p className="text-white/80 text-sm font-medium">{t('terminos')}</p>
                  <p className="text-white/40 text-xs mt-0.5">{t('terminosDesc')}</p>
                </div>
                <VisibilityCheckbox
                  id="show_terminos"
                  value={formData.show_terminos}
                  onChange={(v) => setFormData(prev => ({ ...prev, show_terminos: v }))}
                  disabled={false}
                  label={t('show')}
                />
              </div>

              {/* Derechos del Consumidor */}
              <div className="flex items-start justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div>
                  <p className="text-white/80 text-sm font-medium">{t('derechosConsumidor')}</p>
                  <p className="text-white/40 text-xs mt-0.5">{t('derechosConsumidorDesc')}</p>
                </div>
                <VisibilityCheckbox
                  id="show_derechos_consumidor"
                  value={formData.show_derechos_consumidor}
                  onChange={(v) => setFormData(prev => ({ ...prev, show_derechos_consumidor: v }))}
                  disabled={false}
                  label={t('show')}
                />
              </div>

              {/* Info NOM-247 — solo si nom_candidate = true */}
              {agent?.nom_candidate && (
                <div className="flex items-start justify-between p-3 rounded-xl bg-blue-500/5 border border-blue-500/20">
                  <div>
                    <p className="text-white/80 text-sm font-medium">{t('infoNom247')}</p>
                    <p className="text-white/40 text-xs mt-0.5">{t('infoNom247Desc')}</p>
                    {!(agent?.phone && agent?.email && formData.attention_hours) && (
                      <p className="text-amber-400/70 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />{t('infoNom247Req')}
                      </p>
                    )}
                  </div>
                  <VisibilityCheckbox
                    id="show_info_nom247"
                    value={formData.show_info_nom247}
                    onChange={(v) => setFormData(prev => ({ ...prev, show_info_nom247: v }))}
                    disabled={!(agent?.phone && agent?.email && formData.attention_hours)}
                    label={t('show')}
                  />
                </div>
              )}

            </div>
          </Section>

          {/* ══════════════════════════════════════════════
              SECCIÓN: Redes Sociales
          ══════════════════════════════════════════════ */}
          <Section title={t('sectionSocial')} icon={<Globe className="w-4 h-4 text-pink-400" />} defaultOpen={false}>
            <p className="text-white/40 text-xs -mt-2">{t('socialIntro')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: <Facebook  className="h-4 w-4 text-blue-400"  />, key: 'facebook',  placeholder: 'https://facebook.com/tu-pagina'   },
                { icon: <Instagram className="h-4 w-4 text-pink-400"  />, key: 'instagram', placeholder: 'https://instagram.com/tu-usuario'  },
                { icon: <Linkedin  className="h-4 w-4 text-blue-300"  />, key: 'linkedin',  placeholder: 'https://linkedin.com/in/tu-perfil' },
                { icon: <Youtube   className="h-4 w-4 text-red-500"   />, key: 'youtube',   placeholder: 'https://youtube.com/@tu-canal'    },
              ].map(({ icon, key, placeholder }) => (
                <div key={key} className="flex items-center gap-3">
                  {icon}
                  <Input
                    value={(formData.social_media as any)[key] || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, social_media: { ...prev.social_media, [key]: e.target.value } }))}
                    placeholder={placeholder}
                  />
                </div>
              ))}
              <div className="flex items-center gap-3">
                <svg className="h-4 w-4 text-white/70 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                <Input
                  value={formData.social_media.tiktok || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, social_media: { ...prev.social_media, tiktok: e.target.value } }))}
                  placeholder="https://tiktok.com/@tu-usuario"
                />
              </div>
            </div>
          </Section>

          {/* ══════════════════════════════════════════════
              SECCIÓN: Especialidades y Zonas
          ══════════════════════════════════════════════ */}
          <Section title={t('sectionSpecialties')} icon={<Briefcase className="w-4 h-4 text-green-400" />} defaultOpen={false}>

            {/* Especialidades */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">{t('specialties')}</label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  placeholder={t('newSpecialty')}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('specialties', newSpecialty, setNewSpecialty))}
                />
                <button type="button"
                  onClick={() => addItem('specialties', newSpecialty, setNewSpecialty)}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.specialties.map((s, i) => (
                  <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    {s}
                    <button type="button" onClick={() => removeItem('specialties', i)} className="ml-2 hover:text-white">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10" />

            {/* Zonas */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">{t('zones')}</label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={newZone}
                  onChange={(e) => setNewZone(e.target.value)}
                  placeholder={t('newZone')}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('zones', newZone, setNewZone))}
                />
                <button type="button"
                  onClick={() => addItem('zones', newZone, setNewZone)}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.zones.map((z, i) => (
                  <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-300 border border-purple-500/30">
                    {z}
                    <button type="button" onClick={() => removeItem('zones', i)} className="ml-2 hover:text-white">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

          </Section>

          {/* ── Guardar ── */}
          <div className="flex justify-end pt-2">
            <GradientButton type="submit" disabled={loading}>
              {loading
                ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />{t('saving')}</>
                : <><Save className="h-4 w-4 mr-2" />{t('save')}</>
              }
            </GradientButton>
          </div>

        </form>
      </div>
    </DashboardLayout>
  )
}

export default ProfilePage
